import { memo, useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { Account } from "@common/types/domains/accounting/account";
import { displayAccountLabel } from "@common/helpers/accounting";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    AccrualMatchSuggestionDisplay,
    getFirstDocumentSuggestion,
    getLineItemAccountIdFromSuggestions,
} from "@common/schemas/domains/accrualReview/accrual-match-suggestion-display.schema";
import { getDefaultAccrualAccountId } from "@common/helpers/accrualAccount";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useAccounts } from "@hooks/useAccounts";
import { useEntityIdForGLAccount } from "@hooks/useEntityIdForGLAccount";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { useIsTableReadonly } from "@hooks/useIsReadonlyOrLocked";
import { useHasAccrualLedger, useLedgers } from "@api/ledger.api";
import { getLedgerNameFromColumnId } from "@lib/ledgerViewColumns";
import { readOverrideAccountId } from "@lib/accountOverrides";
import { useAccrualReviewContext } from "./useAccrualReviewContext";
import { useCellActionOpener } from "./useCellActionOpener";
import styles from "./AccrualReviewLedgerCell.module.scss";

export const AccrualReviewLedgerCell = memo(
    ({
        row,
        column,
        table,
    }: CellContext<ViewTableFeatures, TransactionListItem, unknown>) => {
        const transaction = row.original;
        const isTableReadonly = useIsTableReadonly(table);
        const ledgerName = getLedgerNameFromColumnId(column.id);
        const showAccountNumbers = useShowAccountNumbers();
        const {
            getProposedAccountId,
            setProposedAccountId,
            isMatchDismissed,
            hasCellActionOpeners,
            ledgerDisplay,
        } = useAccrualReviewContext();
        const { isViewOnly } = useViewOnlyRefusal();
        const viewOnlyTooltip = useViewOnlyTooltipReason("");
        const [isPickerOpen, setIsPickerOpen] = useState(false);
        const openPicker = useCallback(() => setIsPickerOpen(true), []);

        const entityIdForGLAccount = useEntityIdForGLAccount(transaction);

        const hasAccrualLedger = useHasAccrualLedger(entityIdForGLAccount);
        const { data: ledgers } = useLedgers(entityIdForGLAccount);
        const ledger = useMemo(
            () =>
                ledgerName
                    ? ledgers?.find(
                          (candidate) =>
                              candidate.basis ===
                                  AccountingLedgerBasis.accruals &&
                              candidate.name === ledgerName,
                      )
                    : ledgers?.find(
                          (candidate) =>
                              candidate.basis ===
                                  AccountingLedgerBasis.accruals &&
                              candidate.isPrimary,
                      ),
            [ledgerName, ledgers],
        );
        const accrualLedgers = useMemo(
            () =>
                (ledgers ?? []).filter(
                    (candidate) =>
                        candidate.basis === AccountingLedgerBasis.accruals,
                ),
            [ledgers],
        );

        const { accounts } = useAccounts({
            entityId: hasAccrualLedger ? entityIdForGLAccount : undefined,
            ledgerBasis: AccountingLedgerBasis.accruals,
        });

        const isMatchDismissedForTx = isMatchDismissed(transaction.id);

        const suggestions: AccrualMatchSuggestionDisplay[] = useMemo(
            () =>
                isMatchDismissedForTx
                    ? []
                    : (transaction.accrualMatchSuggestions ?? []),
            [transaction.accrualMatchSuggestions, isMatchDismissedForTx],
        );

        // The account a transfer confirm books this leg to is resolved by the
        // backend (financial-account pairing, credit-card payment, equity,
        // intercompany), so the preview reads it off the suggestion rather than
        // guessing a clearing account. Absent means the pair cannot be booked.
        const transferSuggestion = useMemo(
            () => suggestions.find((s) => s.matchedTransaction),
            [suggestions],
        );

        const hasDocumentSuggestion = useMemo(
            () => suggestions.some((s) => s.invoice ?? s.bill),
            [suggestions],
        );

        const lineItemAccountId = useMemo(
            () => getLineItemAccountIdFromSuggestions(suggestions),
            [suggestions],
        );

        const defaultAccountId = transferSuggestion
            ? (transferSuggestion.accrualAccountId ?? null)
            : getDefaultAccrualAccountId({
                  transaction,
                  accounts,
                  lineItemAccountId,
                  ignoreMatches: !hasDocumentSuggestion,
                  hasDocumentMatch: hasDocumentSuggestion,
              });

        // When a rule has populated a suggested accrual account, prefer that
        // over any other derived default. The match-suggestion fallbacks only
        // apply when the rule did not provide a suggestion.
        const ruleSuggestedAccountId =
            transaction.accrualAccountId == null
                ? (transaction.suggestedAccrualAccountId ?? null)
                : null;

        const proposedAccountId = getProposedAccountId(transaction, ledgerName);
        const acceptedOverrideAccountId = readOverrideAccountId(
            transaction.accountOverrides,
            ledger?.id,
        );
        const primaryAcceptedAccountId = ledger?.isPrimary
            ? transaction.accrualAccountId
            : null;
        const acceptedAccountId =
            primaryAcceptedAccountId ?? acceptedOverrideAccountId;
        const effectiveAccountId =
            acceptedAccountId ??
            proposedAccountId ??
            ruleSuggestedAccountId ??
            defaultAccountId;

        useEffect(() => {
            if (acceptedAccountId || proposedAccountId) {
                return;
            }
            const proposalId = ruleSuggestedAccountId ?? defaultAccountId;
            if (proposalId) {
                setProposedAccountId(transaction.id, proposalId, ledgerName);
            }
        }, [
            acceptedAccountId,
            transaction.id,
            ruleSuggestedAccountId,
            defaultAccountId,
            proposedAccountId,
            setProposedAccountId,
            ledgerName,
        ]);

        const account = useMemo(
            () =>
                effectiveAccountId
                    ? (accounts.find((a) => a.id === effectiveAccountId) ??
                      null)
                    : null,
            [accounts, effectiveAccountId],
        );

        const isAccepted = !!acceptedAccountId;

        const hasActiveSuggestion = suggestions.length > 0;

        const firstDocSuggestion = useMemo(
            () => getFirstDocumentSuggestion(suggestions),
            [suggestions],
        );
        const firstDoc =
            firstDocSuggestion?.invoice ?? firstDocSuggestion?.bill;

        const hasMultipleMatchAccounts =
            firstDoc?.hasMultipleLineItemAccounts ?? false;

        const multipleAccountsTooltip = useMemo(() => {
            const ids = firstDoc?.lineItemAccountIds;
            if (!ids || ids.length === 0) {
                return undefined;
            }
            const labels = ids
                .map((id) => accounts.find((a) => a.id === id))
                .filter((a): a is Account => a != null)
                .map((a) => displayAccountLabel(a, showAccountNumbers));
            if (labels.length === 0) {
                return undefined;
            }
            return (
                <>
                    {labels.map((label, i) => (
                        <div key={i}>{label}</div>
                    ))}
                </>
            );
        }, [firstDoc?.lineItemAccountIds, accounts, showAccountNumbers]);

        const handleChange = useCallback(
            (selectedAccount: Account | null) => {
                if (selectedAccount && !isAccepted) {
                    const shouldUpdateAllLedgers =
                        !ledgerName || ledger?.isPrimary === true;
                    if (shouldUpdateAllLedgers) {
                        setProposedAccountId(
                            transaction.id,
                            selectedAccount.id,
                        );
                        for (const accrualLedger of accrualLedgers) {
                            setProposedAccountId(
                                transaction.id,
                                selectedAccount.id,
                                accrualLedger.name,
                            );
                        }
                        return;
                    }
                    setProposedAccountId(
                        transaction.id,
                        selectedAccount.id,
                        ledgerName,
                    );
                }
            },
            [
                transaction.id,
                isAccepted,
                setProposedAccountId,
                ledgerName,
                ledger,
                accrualLedgers,
            ],
        );

        const isMultipleAccountsShown =
            !isAccepted && !ruleSuggestedAccountId && hasMultipleMatchAccounts;
        const isReadonly =
            isViewOnly ||
            isTableReadonly ||
            isAccepted ||
            (!ruleSuggestedAccountId && hasActiveSuggestion);
        const canOpenPicker =
            hasAccrualLedger &&
            !!entityIdForGLAccount &&
            !isMultipleAccountsShown &&
            !isReadonly;

        useCellActionOpener({
            table,
            columnId: column.id,
            rowId: transaction.id,
            open: canOpenPicker ? openPicker : undefined,
        });

        if (!hasAccrualLedger || !entityIdForGLAccount) {
            return null;
        }

        if (isMultipleAccountsShown) {
            return (
                <ViewTableCell>
                    <EditableCellContent
                        isEmpty={false}
                        readonly
                        tooltip={multipleAccountsTooltip}
                    >
                        <span className={styles.suggestedAccount}>
                            Multiple accounts
                        </span>
                    </EditableCellContent>
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell
                captureClicks
                className={classNames({
                    [styles.fullWidth]: ledgerDisplay === "button",
                })}
            >
                <AccountCombobox
                    showAccountCode={showAccountNumbers}
                    selectedAccount={account ?? undefined}
                    onChange={handleChange}
                    entityId={entityIdForGLAccount}
                    readOnly={isReadonly}
                    disabled={isReadonly}
                    tooltip={viewOnlyTooltip}
                    ledgerBasis={AccountingLedgerBasis.accruals}
                    open={hasCellActionOpeners ? isPickerOpen : undefined}
                    onOpenChange={
                        hasCellActionOpeners ? setIsPickerOpen : undefined
                    }
                    triggerClassName={classNames({
                        [styles.suggestedAccount]: !isAccepted,
                        [styles.inPlace]: ledgerDisplay === "button",
                        [styles.ledgerButton]:
                            ledgerDisplay === "button" &&
                            !isAccepted &&
                            !isReadonly,
                    })}
                />
            </ViewTableCell>
        );
    },
);

AccrualReviewLedgerCell.displayName = "AccrualReviewLedgerCell";
